import React, { useState, useEffect } from "react";
import { Navigate, Outlet, useNavigate, useLocation, useSearchParams, useParams } from "react-router-dom";
import { ApprovalHeader } from "./components/ApprovalHeader";
import { ApprovalErrorState } from "./components/ApprovalErrorState";
import { ApprovalLoadingState } from "./components/ApprovalLoadingState";
import { useApprovalView } from "./hooks/useApprovalView";
import { MediaStage } from "@/core/types/mediaTypes";
import { RoleProvider } from "@/contexts/RoleContext";
import { UserRole } from "@/services/urlService";
import { HttpClientProvider } from "@/core/http/HttpClientContext";
import { createPortalClient } from "@/services/portalClient";
import type { HttpClient } from "@/core/http/types";
import { usePushSetup } from "./push/usePushSetup";
import { InstallBanner } from "./push/InstallBanner";
import { EnableNotificationsCard } from "./push/EnableNotificationsCard";
import { hasPortalApproval, onPortalApproval } from "./push/approvalSignal";
import { TrailDisplayProvider, useDetailTrail } from "@/core/navigation/TrailDisplayContext";
import { BreadcrumbTrail } from "@/components/Header/BreadcrumbTrail";
import { useLocale } from "@/hooks/useLocale";

interface ApprovalViewProps {
  clientId?: string;
}

/**
 * Publishes the two-crumb drill-down trail ([Dashboard, current page]) to
 * the shared portal header while a subroute with a page title is mounted.
 * Replaces the old header back-chevron (DD-025).
 */
const ApprovalTrailPublisher: React.FC<{ clientId: string; title: string }> = ({
  clientId,
  title,
}) => {
  const { t } = useLocale();
  useDetailTrail({
    fallback: { to: `/client/${clientId}`, label: t("client.trail.dashboard") },
    title,
  });
  return null;
};

type ApprovalTab = 'foto' | 'reel';

export const ApprovalView: React.FC<ApprovalViewProps> = ({ clientId: propClientId }) => {
  const params = useParams<{ clientId: string }>();
  const clientId = propClientId || params.clientId!;
  // Stable portal HttpClient instance per client id. Role is fixed to
  // 'client' for the approval portal; editor uses a separate route tree.
  const portalHttp = React.useMemo(
    () => createPortalClient(clientId, "client") as unknown as HttpClient,
    [clientId]
  );
  const { t } = useLocale();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  // Get media stage filter from URL or default to undefined (all items)
  const mediaStage = searchParams.get('stage') || undefined;

  // Determine active tab based on route (undefined for dashboard)
  const getActiveTab = (): ApprovalTab | undefined => {
    if (location.pathname.includes('/reel')) return 'reel';
    if (location.pathname.includes('/review/media')) return 'foto';
    return undefined;
  };
  const activeTab = getActiveTab();

  // Determine content type based on active tab
  const contentType = activeTab === 'reel' ? 'reels' : activeTab === 'foto' ? 'fotos' : undefined;

  // Only apply stage and content filters when on a specific tab (not dashboard)
  // Dashboard needs ALL media to count pending items
  const effectiveMediaStage = activeTab ? mediaStage : undefined;
  const effectiveContentType = activeTab ? contentType : undefined;

  // Determine if we need to fetch initial data based on the route
  // Routes like planning, report, social-media don't need posts/media data
  const needsInitialData = !location.pathname.includes('/planning') &&
                           !location.pathname.includes('/piano') &&
                           !location.pathname.includes('/social-media') &&
                           !location.pathname.includes('/report') &&
                           !location.pathname.includes('/tasks') &&
                           !location.pathname.includes('/bio') &&
                           !location.pathname.includes('/foto');

  const {
    isAuthenticated,
    isCheckingSession,
    approvalData,
    pendingMedia,
    pendingPosts,
    approvedPosts,
    isLoading,
    isInitialLoading,
    error,
    handlePostUpdate,
    handleReviewPost,
    rateMedia,
    updateMediaLabel,
    fetchDataForTab,
    loadMoreMedia,
    hasNext,
    totalCount,
    token,
    logout,
  } = useApprovalView({
    clientId,
    mediaStage: effectiveMediaStage,
    contentType: effectiveContentType,
    shouldFetchInitialData: needsInitialData,
  });

  const [contentReady, setContentReady] = useState(false);

  // Self-healing push subscribe on every portal visit (no-ops without
  // granted permission). Called unconditionally to respect hooks rules;
  // the hook guards on an empty clientId.
  const { permission, enable, unsubscribe } = usePushSetup(clientId);

  // User-initiated logout: best-effort server-side unsubscribe before
  // clearing auth so a signed-out device stops receiving pushes.
  const handleLogout = async () => {
    await unsubscribe();
    logout();
  };

  // Show the permission ask only after the client has completed a value
  // moment (an approval/rating). Event-driven: child-route mutations
  // (idea reviews) don't re-render this component, so a plain
  // sessionStorage read here would miss them until navigation.
  const [didApprove, setDidApprove] = useState(hasPortalApproval);
  useEffect(() => onPortalApproval(() => setDidApprove(true)), []);

  // Determine page title based on route
  const getPageTitle = (): string | undefined => {
    if (location.pathname.includes('/reel')) return 'Reel';
    if (location.pathname.includes('/review/media')) return 'Foto';
    if (location.pathname.includes('/foto')) return 'Photographer';
    if (location.pathname.includes('/posts')) return 'Revisiona Post';
    if (location.pathname.includes('/ideas')) return 'Revisiona Idee';
    if (location.pathname.includes('/planning')) return 'Planning';
    if (location.pathname.includes('/social-media')) return 'Account Social';
    if (location.pathname.includes('/report')) return 'Report Social Media';
    if (location.pathname.includes('/piano')) return t('plan.title');
    if (location.pathname.includes('/tasks')) return t("client.tasks.title");
    if (location.pathname.includes('/bio')) return 'Bio';
    return undefined;
  };
  const pageTitle = getPageTitle();

  // Handler to navigate back to dashboard
  const handleBackToDashboard = () => {
    navigate(`/client/${clientId}`);
  };

  // Handle authentication state transitions
  // Keep loader visible until content is ready to show
  useEffect(() => {
    // Some routes (like planning, report, social-media) don't need approvalData
    // Dashboard and foto tab need it
    const isDashboard = !location.pathname.includes('/review/media') &&
                       !location.pathname.includes('/piano') &&
                       !location.pathname.includes('/foto') &&
                       !location.pathname.includes('/reel') &&
                       !location.pathname.includes('/planning') &&
                       !location.pathname.includes('/social-media') &&
                       !location.pathname.includes('/report') &&
                       !location.pathname.includes('/tasks') &&
                       !location.pathname.includes('/bio');

    const needsApprovalData = activeTab !== undefined || isDashboard;

    const isReady = needsApprovalData
      ? isAuthenticated && approvalData && !isInitialLoading
      : isAuthenticated && !isInitialLoading;

    if (isReady) {
      // Small delay to ensure smooth transition from loader to content
      const timer = setTimeout(() => {
        setContentReady(true);
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setContentReady(false);
    }
  }, [isAuthenticated, approvalData, isInitialLoading, activeTab, location.pathname]);

  // Fetch data when tab changes
  useEffect(() => {
    if (isAuthenticated && !isCheckingSession && activeTab) {
      fetchDataForTab(activeTab);
    }
  }, [activeTab, isAuthenticated, isCheckingSession, fetchDataForTab]);


  // Show loading while checking session
  if (isCheckingSession) {
    return (
      <div className="transition-opacity duration-500 ease-in-out">
        <ApprovalLoadingState />
      </div>
    );
  }

  // Not authenticated: bounce to the self-serve magic-link request page,
  // preserving the originating path as `?redirect=` so the user lands on
  // the requested resource after authenticating. Admins can still
  // deep-link into `/collaborators/:clientId/auth?token=...`.
  if (!isAuthenticated) {
    const originPath = `${location.pathname}${location.search}`;
    const loginUrl = originPath && originPath !== "/portal/login"
      ? `/portal/login?redirect=${encodeURIComponent(originPath)}`
      : "/portal/login";
    return <Navigate to={loginUrl} replace />;
  }

  // Show loading state until content is ready
  if (!contentReady) {
    return (
      <div className="transition-opacity duration-500 ease-in-out">
        <ApprovalLoadingState />
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="transition-all duration-500 ease-in-out">
        <ApprovalErrorState error={error.message} onRetry={() => activeTab && fetchDataForTab(activeTab)} />
      </div>
    );
  }


  return (
    <HttpClientProvider client={portalHttp}>
    <TrailDisplayProvider>
    <div className="flex h-[100dvh] flex-col bg-gray-50 animate-in fade-in duration-500 overflow-x-hidden">
      {/* Header — fixed in flex flow so it never moves. Routes below own
          their own scroll; that keeps sticky/fixed children inside routes
          honest about viewport positioning. Transparent, no divider — the
          inner gutter matches the dashboard content column (DD-025). */}
      <div className="shrink-0 sticky top-0 z-40 bg-gray-50 animate-in fade-in slide-in-from-top-2 duration-400" style={{animationDelay: '100ms', animationFillMode: 'backwards'}}>
        <div className="max-w-2xl mx-auto px-4 lg:px-6 py-3">
          <ApprovalHeader
            clientName={approvalData?.clientName || ''}
            onLogout={handleLogout}
            menuItems={[
              { label: t("client.menu.tasks"), onSelect: () => navigate(`/client/${clientId}/tasks`) },
            ]}
          />
          {/* Drill-down trail replaces the old header back-chevron. */}
          <BreadcrumbTrail />
        </div>
      </div>

      {/* Publishes [Dashboard, pageTitle] to the header trail on subroutes.
          The media/reel review routes publish their own grid<->feed trail
          (ApprovalMediaRoute), so skip them here to avoid a double-publish. */}
      {pageTitle && !activeTab && <ApprovalTrailPublisher clientId={clientId} title={pageTitle} />}

      {/* Nested route content — flex-1 with min-h-0 lets routes use a
          flex-col + overflow-y-auto pattern without body overflow. */}
      <div className="flex flex-1 min-h-0 flex-col animate-in fade-in slide-in-from-bottom-2 duration-500" style={{animationDelay: '200ms', animationFillMode: 'backwards'}}>
        <RoleProvider role={UserRole.CLIENT} clientId={clientId} token={token}>
          <InstallBanner />
          {didApprove && (
            <EnableNotificationsCard permission={permission} onEnable={enable} />
          )}
          <Outlet context={{
              pendingPosts,
              approvedPosts,
              pendingMedia,
              clientId,
              token,
              onUpdate: handlePostUpdate,
              onReviewPost: handleReviewPost,
              onRateMedia: rateMedia,
              onUpdateMediaLabel: updateMediaLabel,
              loadMoreMedia,
              hasNext,
              totalCount,
              isLoading,
              mediaStage,
              onMediaStageChange: (stage?: string) => {
                if (stage) {
                  searchParams.set('stage', stage);
                } else {
                  searchParams.delete('stage');
                }
                setSearchParams(searchParams);
              },
            }} />
        </RoleProvider>
      </div>
    </div>
    </TrailDisplayProvider>
    </HttpClientProvider>
  );
};
